import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RootState, useAppDispatch } from "../../../redux/store";
import { useSelector } from "react-redux";
import TimeEntryModal from "./TimeEntryModal";
import ApprovalDetailsSection from "./ApprovalDetailsSection";
import {
    getEmployeeTimeCardGrouped,
    // Commented out: unused summary APIs - responses not displayed in UI
    // getEmployeeTimeCardSummary,
    // getThisWeekTimeCardSummary,
    getTodayTimeCardSummary,
    getTimecardRequests,
} from "../../../redux/features/EmployeeTimeCard";
import { getPayperiod } from "../../../redux/features/payperiod";
import { convertDecimalHoursToHM, getCurrentTime } from "../../../utils/employeeTimeCardhelper";
import { Loader, ArrowLeft, ArrowRight, Users, Clock, CheckCircle } from "lucide-react";
import Select from "react-select";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import phpClient from "../../../network/phpClient";
import { toast } from "react-toastify";
import { getTimecardApprovalDetails } from "../../../redux/features/schedule/ScheduleApi";
import { fetchLastPunch } from "../../../redux/features/common";
import { getManagerEmployees } from "../../../redux/features/timesheet/api";
import { ManagerTimesheetEmployee } from "../../../types";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { setGlobalBackHandler, clearGlobalBackHandler } from "../../../hooks";
import { isPrivilege, UserPrivileges } from "../../../rules";
import { DateTimeUtil, TimecardRequestUtil } from "../../../utils";
import RequestsAndAdjustmentsCard from "./RequestsAndAdjustmentsCard";
import { TimecardRequestItem } from "../../../types/response/timecard";
import MobileTimesheetEntryRow from "./MobileTimesheetEntryRow";
import { buildPayperiodOptions, resolveAutoSelectedPayperiod } from "../../../utils/payPeriodSelection";

interface TimeEntry {
    date: string;
    day: string;
    duration: string;
    timeRange?: string;
    approved?: boolean;
}

const TimecardMobileView: React.FC = () => {
    const getRowBgClassForTimeoff = (apiColor?: string) => {
        let color = "red";
        if (apiColor) {
            const match = apiColor.match(/bg-([a-z]+)-\d+/);
            if (match && match[1]) {
                color = match[1];
            }
        }
        return `bg-${color}-50 hover:bg-${color}-100 cursor-default`;
    };

    const resolveRowBgClassForTimeoff = (entry: any) => {
        const defaultBgClass = "bg-gray-50 hover:bg-gray-100 cursor-pointer";

        if (entry?.record_type === "A" && entry?.other_details?.POLICY_COLOR) {
            return getRowBgClassForTimeoff(entry?.other_details?.POLICY_COLOR);
        }

        if(entry?.record_type === "T") {
            return getRowBgClassForTimeoff();
        }
        return defaultBgClass;
    };

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { setSourcePath } = useNavigationHistory();
    const user = useSelector((state: RootState) => state?.user?.user);
    const { todaySummary, groupedTimesheets } = useSelector((state: RootState) => state.employeeTimeCardData);
    const { payperiodFilters } = useSelector((state: RootState) => state.payperiod);
    const lastPunchData = useSelector((state: RootState) => state.common.LastPunchState);
    const { employeeRecords } = useSelector((state: RootState) => state.timesheet);

    // State for manager view
    const [currentEmployeeIndex, setCurrentEmployeeIndex] = useState(0);
    // const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");

    const localStoragePayperiod = localStorage.getItem("selectedPayperiod") || "";
    const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedStartDate, setSelectedStartDate] = useState<string>("");
    const [selectedEndDate, setSelectedEndDate] = useState<string>("");
    const [selectedPayperiod, setSelectedPayperiod] = useState<string>(localStorage.getItem("selectedPayperiod") || "");
    const [loadMoreCount, setLoadMoreCount] = useState<number>(-5);
    const [payperiodOptions, setPayperiodOptions] = useState<any[]>([]);
    const prevStartDateRef = useRef<string>("");
    const prevEndDateRef = useRef<string>("");
    const [loading, setLoading] = useState<boolean>(true); // Loading for general data
    const [loadingPayperiod, setLoadingPayperiod] = useState<boolean>(false); // Loading for payperiod load more
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [timecardRequests, setTimecardRequests] = useState<TimecardRequestItem[]>([]);

    const isAdmin = useMemo(
        () =>
            isPrivilege({
                privileges: user?.employee_access_role_privileges,
                rule: UserPrivileges.EMPLOYEE_PRIV_ADMIN,
            }),
        [user?.employee_access_role_privileges]
    );

    const isAllowedToEdit = useMemo(
        () =>
            isPrivilege({
                privileges: user?.employee_access_role_privileges,
                rule: UserPrivileges.EMPLOYEE_PRIV_UPDATE_TIMESHEET,
            }),
        [user?.employee_access_role_privileges]
    );

    const isManagerPrivilege = useMemo(
        () =>
            isPrivilege({
                privileges: user?.employee_access_role_privileges,
                rule: UserPrivileges.EMPLOYEE_PRIV_UPDATE_TIMESHEET,
            }),
        [user?.employee_access_role_privileges]
    );

    const isManager = useMemo(() => user?.is_manager, [user?.is_manager]);

    const selfPrivilege = isAdmin || isAllowedToEdit;
    const managerPrivilege = isAdmin || isManagerPrivilege;

        const canEdit = isManager ? managerPrivilege : selfPrivilege;

    const approvalStatus = (groupedTimesheets?.data?.approval_status || "").toLowerCase();
    const shouldApprovalBtn = approvalStatus === "pending";
    const offCycleAllowed = !!((groupedTimesheets as any)?.data?.off_cycle_approval_allowed);

    const isAllowedToRequest = TimecardRequestUtil.isAllowed({
        allowed: user?.attendance_hours_request_allowed,
        configs: user?.ATTENDANCE_REQUEST_CONFIG?.enable_for,
        payperiods: payperiodFilters,
    });

    // Employee navigation handlers for manager view
    const handlePreviousEmployee = () => {
        if (employeeRecords && employeeRecords.length > 0) {
            setCurrentEmployeeIndex((prev) => (prev > 0 ? prev - 1 : employeeRecords.length - 1));
        }
    };

    const handleNextEmployee = () => {
        if (employeeRecords && employeeRecords.length > 0) {
            setCurrentEmployeeIndex((prev) => (prev < employeeRecords.length - 1 ? prev + 1 : 0));
        }
    };

    const handleLoadMore = () => {
        if (user?.os_emp_account_id) {
            const newCount = loadMoreCount - 5;
            setLoadMoreCount(newCount);
            setLoadingPayperiod(true);
            dispatch(getPayperiod({ employee_id: user?.os_emp_account_id, count: newCount })).finally(() => {
                setLoadingPayperiod(false); // Stop payperiod loading once it's done
            });
        }
    };

    useEffect(() => {
        if (user?.os_emp_account_id && !payperiodFilters?.length) {
            // setLoading(true);
            dispatch(getPayperiod({ employee_id: user?.os_emp_account_id, count: loadMoreCount })).finally(() => {
                // setLoading(false); // Stop loading after initial data fetch
            });
        }
    }, [dispatch, user?.os_emp_account_id, loadMoreCount, payperiodFilters?.length]);

    useEffect(() => {
        dispatch(setTopNavNameTitle("Timesheet"));
        const datetime = getCurrentTime(); // example datetime
        dispatch(fetchLastPunch(datetime));

        // Set up proper back navigation - this is the key fix
        // Remember where we came from (likely /mobile-dashboard)
        if (location.state?.from) {
            setSourcePath(location.state.from);
        } else {
            setSourcePath("/mobile-dashboard");
        }

        // Set up global back handler to go back to mobile dashboard
        setGlobalBackHandler(() => {
            navigate("/mobile-dashboard");
            return true; // prevent default back behavior
        });

        // Clean up when component unmounts
        return () => {
            clearGlobalBackHandler();
        };
    }, [dispatch, navigate, location.state, setSourcePath]);

    //reset local storage if payperiod on date change
    useEffect(() => {
        if (payperiodFilters?.length) {
            const newPayperiodOptions = buildPayperiodOptions(payperiodFilters);
            const resolvedPayperiod = resolveAutoSelectedPayperiod({
                payperiodOptions: newPayperiodOptions,
                locationState: location.state as
                    | { payPeriod?: string; payPeriodStart?: string; payPeriodEnd?: string; targetDate?: string }
                    | null,
                storageKey: "selectedPayperiod",
                storageDateKey: "selectedPayperiodDate",
            });

            if (resolvedPayperiod) {
                setSelectedPayperiod(resolvedPayperiod);
                localStorage.setItem("selectedPayperiod", resolvedPayperiod);
                const [startDatetime, endDatetime] = resolvedPayperiod.split(" - ");
                handlePayperiodChange(startDatetime, endDatetime);
            }

            setPayperiodOptions([...newPayperiodOptions, { value: "load-more", label: "Load More" }]);
        }
    }, [location.state, payperiodFilters]);

    const handlePayperiodChange = (startDate: string, endDate: string) => {
        // Avoid unnecessary API calls if the start and end date are the same as the previous ones
        if (startDate === prevStartDateRef.current && endDate === prevEndDateRef.current) {
            return;
        }

        // Set loading state to true when starting the API call
        setLoading(true);

        // Update the selected start and end date
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);

        // Update the refs to prevent unnecessary API calls on subsequent renders
        prevStartDateRef.current = startDate;
        prevEndDateRef.current = endDate;

        const id = Number(user?.os_emp_account_id);
        // Create an array of promises for the API calls
        const apiCalls = [
            dispatch(
                getEmployeeTimeCardGrouped({
                    start_date: startDate,
                    end_date: endDate,
                    employee_id: user?.os_emp_account_id,
                    // record_type: "A",
                    approval_status: "",
                    sort_by: "os_emp_start_datetime",
                    sort_order: "ASC",
                    group_by: "date",
                    page: "1",
                })
            ),
            dispatch(getTodayTimeCardSummary(id)),
            // Commented out: getThisWeekTimeCardSummary - response not displayed in UI
            // dispatch(getThisWeekTimeCardSummary(id)),
            ...(user?.os_emp_account_id
                ? [
                      dispatch(
                          getTimecardRequests({
                              start_date: startDate,
                              end_date: endDate,
                              employee_id: String(user.os_emp_account_id),
                          })
                      ),
                  ]
                : []),
        ];

        // Wait for all API calls to complete
        Promise.all(apiCalls)
            .then((results: any[]) => {
                const requestsResult = user?.os_emp_account_id ? results[2] : undefined;
                const data = requestsResult?.payload?.data;
                setTimecardRequests(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error with API calls:", error);
                setTimecardRequests([]);
                setLoading(false);
            });
    };

    // Commented out: getEmployeeTimeCardSummary - response not displayed in UI
    // Pay Period Summary uses groupedTimesheets?.data?.pay_period_totals?.total_hours instead
    // useEffect(() => {
    //     if (user?.os_emp_account_id && payperiodFilters?.length > 0 && !localStoragePayperiod) {
    //         setLoading(true);
    //         dispatch(
    //             getEmployeeTimeCardSummary({
    //                 employee_id: Number(user?.os_emp_account_id),
    //                 start_date: payperiodFilters[0]?.start_datetime,
    //                 end_date: payperiodFilters[0]?.end_datetime,
    //             })
    //         ).finally(() => setLoading(false));
    //     }
    // }, [dispatch, user?.os_emp_account_id, payperiodFilters]);

    const handlePayperiodDropdownChange = (selectedOption: any) => {
        if (selectedOption.value === "load-more") {
            handleLoadMore();
        } else {
            setSelectedPayperiod(selectedOption?.value);
            localStorage.setItem("selectedPayperiod", selectedOption?.value);
            const [startDatetime, endDatetime] = selectedOption.value.split(" - ");
            handlePayperiodChange(startDatetime, endDatetime);
        }
    };

    // // When employee records change or current index changes, update the selected employee
    // useEffect(() => {
    //     if (employeeRecords && employeeRecords.length > 0) {
    //         const employee = employeeRecords[currentEmployeeIndex];
    //         if (employee && employee.emp_detail?.employee_id) {
    //             setSelectedEmployeeId(employee.emp_detail.employee_id);
    //         }
    //     }
    // }, [employeeRecords, currentEmployeeIndex]);

    // When selected employee changes, fetch their timecard data
    // useEffect(() => {
    //     if (selectedEmployeeId && selectedStartDate && selectedEndDate) {
    //         dispatch(
    //             getEmployeeTimeCardGrouped({
    //                 start_date: selectedStartDate,
    //                 end_date: selectedEndDate,
    //                 employee_id: selectedEmployeeId,
    //                 // record_type: "A",
    //                 approval_status: "",
    //                 sort_by: "os_emp_start_datetime",
    //                 sort_order: "ASC",
    //                 group_by: "date",
    //                 page: "1",
    //             })
    //         );
    //     }
    // }, [selectedEmployeeId, selectedStartDate, selectedEndDate, dispatch]);

    return (
        <div id="refresh-container" className="h-full overflow-y-auto bg-gray-50">
            {/* Responsive container - wider on desktop */}
            {/* <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"> */}
            <div className="w-full max-w-5xl mx-auto bg-white px-4 py-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    {/* Header Section */}
                    <div className="border-b border-gray-200 px-6 py-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                            <div className="mb-4 sm:mb-0">
                                <h1 className="text-2xl font-bold text-gray-900">Timesheet</h1>
                                <p className="text-sm text-gray-600 mt-1">
                                    {user?.os_emp_first_name} {user?.os_emp_last_name}
                                </p>
                            </div>

                            {/* Pay Period Selector - Responsive */}
                            <div className="w-full sm:w-80">
                                <Select
                                    inputId="payperiodDropdown"
                                    value={payperiodOptions?.find((option) => option?.value === selectedPayperiod)}
                                    onChange={handlePayperiodDropdownChange}
                                    options={payperiodOptions}
                                    className="block w-full"
                                    classNamePrefix="react-select"
                                    isSearchable={false}
                                    placeholder="Select a pay period"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <div className="flex justify-center items-center py-12">
                            <Loader
                                size={32}
                                color="#2664eb"
                                strokeWidth={2.5}
                                absoluteStrokeWidth
                                className="animate-spin"
                            />
                        </div>
                    )}

                    {!loading && (
                        <div className="p-6">
                            {/* Summary Cards - Responsive Grid */}
                            <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-6 mb-8">
                                {/* Today's Summary */}
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                                    <div className="text-center">
                                        <div className="text-3xl font-bold text-blue-600 mb-2">
                                            {/* {convertDecimalHoursToHM(todaySummary?.total_hours ?? 0)} */}
                                            {/* {convertDecimalHoursToHM(groupedTimesheets?.data?.pay_period_totals?.total_hours ?? 0)} */}
                                            {groupedTimesheets?.data?.today_totals?.total_hours_string ?? 0}
                                        </div>
                                        <div className="text-lg font-medium text-gray-900 mb-2">Today</div>
                                        <div className="text-sm text-gray-600">
                                            {lastPunchData && lastPunchData.datetime && lastPunchData.punch_status ? (
                                                <>
                                                    Clocked {lastPunchData.punch_status === "0" ? "In" : "Out"}:{" "}
                                                    {DateTimeUtil.getFormattedTime(lastPunchData.datetime || "")},{" "}
                                                    {DateTimeUtil.formatShortDate(lastPunchData.datetime || "")}
                                                </>
                                            ) : (
                                                "Last punch data not found"
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Pay Period Summary */}
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border border-green-200">
                                    <div className="text-center">
                                        <div className="text-3xl font-bold text-green-600 mb-2">
                                            {/* {convertDecimalHoursToHM(
                                                groupedTimesheets?.data?.pay_period_totals?.total_hours ?? 0
                                            )} */}
                                            {groupedTimesheets?.data?.pay_period_totals?.total_hours_string}
                                        </div>
                                        <div className="text-lg font-medium text-gray-900 mb-2">Pay Period</div>
                                        <div className="text-sm text-gray-600">Total hours this period</div>
                                    </div>
                                </div>

                                {/* Approval Status Card */}
                                {groupedTimesheets?.data?.approval_details &&
                                    groupedTimesheets.data.approval_details.length > 0 && (
                                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                                            <div className="text-center">
                                                <div className="text-lg font-medium text-gray-900 mb-3">
                                                    Approval Status
                                                </div>
                                                <div className="space-y-2">
                                                    {(() => {
                                                        const approvalStatus = (groupedTimesheets.data.approval_status || "").toUpperCase();
                                                        const isProcessed = approvalStatus === "PROCESSED";
                                                        const approvedCount = groupedTimesheets.data.approval_details.filter(
                                                            (a) => {
                                                                const statusCode = a.current_status_code_txn;
                                                                return statusCode !== undefined && statusCode !== null && (statusCode === 1 || Number(statusCode) === 1);
                                                            }
                                                        ).length;
                                                        const totalCount = groupedTimesheets.data.approval_details.length;
                                                        const allApproved = approvedCount === totalCount;
                                                        
                                                        return (
                                                            <>
                                                                <div className="flex items-center justify-center space-x-2 whitespace-nowrap">
                                                                    {isProcessed || allApproved ? (
                                                                        <>
                                                                            <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
                                                                            <span className="text-green-600 font-medium">Processed</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <Clock className="text-blue-600 flex-shrink-0" size={20} />
                                                                            <span className="text-blue-600 font-medium">In Progress</span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                <div className="text-sm text-gray-700">
                                                                    <span className="text-blue-600 font-medium">
                                                                        {approvedCount}
                                                                    </span>{" "}
                                                                    of{" "}
                                                                    <span className="text-blue-600 font-medium">
                                                                        {totalCount}
                                                                    </span>{" "}
                                                                    approved
                                                                </div>
                                                                {!isProcessed && !allApproved && (
                                                                    <div className="text-sm text-gray-600">
                                                                        Waiting{" "}
                                                                        <span className="text-blue-600">
                                                                            on{" "}
                                                                            {(() => {
                                                                                const pendingApproval = groupedTimesheets.data.approval_details.find(
                                                                                    (a) => {
                                                                                        const statusCode = a.current_status_code_txn;
                                                                                        return statusCode !== undefined && statusCode !== null && (statusCode === 0 || Number(statusCode) === 0);
                                                                                    }
                                                                                );
                                                                                const approverNames =
                                                                                    pendingApproval?.wf_task_actor?.EMPLOYEE?.map(
                                                                                        (emp) => emp.employee_name
                                                                                    ).join(", ") || "Approver";
                                                                                return approverNames;
                                                                            })()}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                {/* Action Card */}
                                <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-6 rounded-lg border border-gray-200">
                                    <div className="text-center">
                                        <div className="text-lg font-medium text-gray-900 mb-4 space-y-3">Actions</div>
                                        {canEdit && (
                                            <div className="space-y-3">
                                                <button
                                                    className="flex w-full min-w-0 items-center justify-center rounded-lg border border-blue-600 bg-transparent px-4 py-3 text-center text-sm font-medium leading-tight text-blue-600 transition-colors duration-200 whitespace-normal break-words"
                                                    onClick={() => navigate("/m-add-timesheet-entry")}
                                                >
                                                    Add Entry
                                                </button>

                                                {isAllowedToRequest && (
                                                    <button
                                                        className="flex w-full min-w-0 items-center justify-center rounded-lg border border-green-600 bg-transparent px-4 py-3 text-center text-sm font-medium leading-tight text-green-600 transition-colors duration-200 whitespace-normal break-words"
                                                        onClick={() => navigate("/m-timecard-request")}
                                                    >
                                                        Request
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        {offCycleAllowed && (
                                            <button
                                                className="mt-3 mb-3 flex w-full min-w-0 items-center justify-center rounded-lg border border-blue-600 bg-transparent px-4 py-3 text-center text-sm font-medium leading-tight text-blue-600 whitespace-normal break-words"
                                                onClick={async () => {
                                                    try {
                                                        setLoading(true);
                                                        const payload = {
                                                            start_date: selectedStartDate,
                                                            end_date: selectedEndDate,
                                                            APPROVAL_STATUS: 1,
                                                            APPROVAL_NOTE: "",
                                                            EMP_IDS: [Number(user?.os_emp_account_id)],
                                                            WF_CODE: (groupedTimesheets as any)?.data?.WF_CODE,
                                                        };

                                                        await phpClient.post({ url: "/timecard/approval_off_cycle", data: payload });
                                                        toast.success("Off-cycle approval request sent");

                                                        await Promise.all([
                                                            dispatch(
                                                                getEmployeeTimeCardGrouped({
                                                                    start_date: selectedStartDate,
                                                                    end_date: selectedEndDate,
                                                                    employee_id: user?.os_emp_account_id,
                                                                    approval_status: "",
                                                                    sort_by: "os_emp_start_datetime",
                                                                    sort_order: "ASC",
                                                                    group_by: "date",
                                                                    page: "1",
                                                                })
                                                            ),
                                                            dispatch(
                                                                getTimecardApprovalDetails({
                                                                    start_date: selectedStartDate,
                                                                    end_date: selectedEndDate,
                                                                    extra_filters: ["employees_list", "reject_list"],
                                                                })
                                                            ),
                                                        ]);
                                                    } catch (e) {
                                                        console.error("approval_off_cycle error", e);
                                                        toast.error("Failed to send off-cycle approval request");
                                                    } finally {
                                                        setLoading(false);
                                                    }
                                                }}
                                            >
                                                Approve Off-Cycle
                                            </button>
                                        )}

                                        {shouldApprovalBtn && (
                                            <button
                                                className="mt-3 flex w-full min-w-0 items-center justify-center rounded-lg border border-blue-600 bg-transparent px-4 py-3 text-center text-sm font-medium leading-tight text-blue-600 whitespace-normal break-words"
                                                onClick={() => {
                                                    navigate(
                                                        `/m-timesheet-approval/${selectedStartDate}/${selectedEndDate}/${user?.os_emp_account_id}`
                                                    );
                                                }}
                                            >
                                                Approval
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Timesheet Entries - Responsive Layout */}
                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-8">
                                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900">Time Entries</h2>
                                </div>

                                <div className="divide-y divide-gray-200">
                                    {groupedTimesheets?.data?.timesheet_entries_by_date &&
                                    Object.keys(groupedTimesheets.data.timesheet_entries_by_date).length === 0 ? (
                                        <div className="px-6 py-12 text-center text-gray-500">
                                            No timecard data available.
                                        </div>
                                    ) : (
                                        Object.entries(groupedTimesheets?.data?.timesheet_entries_by_date || {}).map(
                                            ([dateKey, entries]) => {
                                                const totalHours = entries.reduce(
                                                    (acc, entry) => acc + (Number(entry?.hours) || 0),
                                                    0
                                                );
                                                return (
                                                    <div key={dateKey} className="p-6">
                                                        {/* Date Header */}
                                                        <div className="flex items-center justify-between mb-4">
                                                            <div className="flex items-center space-x-4">
                                                                <div className="text-xl font-semibold text-blue-600">
                                                                    {DateTimeUtil.formatShortDate(dateKey)}
                                                                </div>
                                                                <div className="text-lg font-medium text-gray-700">
                                                                    {convertDecimalHoursToHM(totalHours)}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Entries List */}
                                                        <div className="space-y-3">
                                                            {entries.map((entry: any, index: number) => (
                                                                <MobileTimesheetEntryRow
                                                                    key={index}
                                                                    entry={entry}
                                                                    rowClassName={resolveRowBgClassForTimeoff(entry)}
                                                                    onClick={() => {
                                                                        if (entry?.record_type !== "T") {
                                                                            navigate(
                                                                                `/m-timecard-record-detail/${entry?.id}`
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        )
                                    )}
                                </div>
                            </div>

                            <RequestsAndAdjustmentsCard requests={timecardRequests} loading={loading} />

                            {/* Approval Details Section */}
                            <ApprovalDetailsSection
                                approvalDetails={groupedTimesheets?.data?.approval_details || []}
                            />
                        </div>
                    )}

                    {isModalOpen && <TimeEntryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />}
                </div>
            </div>
        </div>
    );
};

export default TimecardMobileView;
